/**
 * Axios-based HTTP client for PATH borrower portal.
 * - Base URL from VITE_API_BASE_URL
 * - Auth token injection via request interceptor
 * - Typed error responses
 * - Retry logic (3 retries, exponential backoff)
 */

import type { ApiError } from '@/types/auth';
import { toast } from '@/hooks/use-toast';
import { logRawError, mapErrorToUserMessage, withUserFriendlyError, type UserFacingError } from '@/lib/errors/userMessages';

// ---------- Config ----------

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 300;
// P0 WHITE-PAGE FIX: default fetch timeout to prevent API hangs that keep
// isLoading=true forever (Azure SQL cold start, network issues, etc.).
// Each request creates its own AbortController; callers may still pass a signal
// to override or cancel independently.
const DEFAULT_FETCH_TIMEOUT_MS = 30_000;

// ---------- Token access ----------

// Default token accessor that fails-closed instead of returning a mock string
let getTokenFn: (() => Promise<string | null>) = async () => {
  // Fail-closed: throw error instead of silently returning null or a mock string
  throw new Error('No authentication provider configured. Please ensure auth is properly initialized.');
};
let refreshTokenFn: (() => Promise<{ accessToken: string }>) | null = null;
let onUnauthorizedFn: (() => void) | null = null;

/** Shared refresh promise to prevent concurrent refresh races */
let activeRefreshPromise: Promise<{ accessToken: string }> | null = null;

/** Parse JWT to get expiration time (seconds since epoch) */
function getTokenExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch (err) {
    // BUG-DEEP silent-catch fix: Log malformed JWT so debugging is easier.
    console.warn('[api-client] Failed to parse JWT expiry — token may be malformed:', err);
    return null;
  }
}

/** Check if token expires within the given threshold (seconds) */
function isTokenExpiringSoon(token: string, thresholdSecs = 60): boolean {
  const exp = getTokenExp(token);
  if (exp === null) return false;
  return (exp - Date.now() / 1000) < thresholdSecs;
}

/** Allow the auth layer to inject a token accessor */
export function setTokenAccessor(fn: () => Promise<string | null>): void {
  getTokenFn = fn;
}

/** Allow the auth layer to inject a token refresh function */
export function setTokenRefresher(fn: () => Promise<{ accessToken: string }>): void {
  refreshTokenFn = fn;
}

/** Callback when auth is fully expired (redirect to login) */
export function setOnUnauthorized(fn: () => void): void {
  onUnauthorizedFn = fn;
}

// ---------- Error helpers ----------

export class ApiRequestError extends Error {
  status: number;
  body: ApiError;
  rawMessage?: string;
  userMessage?: UserFacingError;
  toastShown?: boolean;
  constructor(status: number, body: ApiError) {
    super(body.message || `Request failed with status ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
    this.rawMessage = this.message;
    this.userMessage = mapErrorToUserMessage(this);
    this.message = this.userMessage.message;
  }
}

function isRetryable(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/** Parse Retry-After header (seconds or HTTP-date) into ms delay */
function parseRetryAfter(res: Response): number | null {
  const header = res.headers.get('Retry-After');
  if (!header) return null;
  const secs = Number(header);
  if (!Number.isNaN(secs) && secs > 0) return secs * 1000;
  // Try HTTP-date format
  const date = Date.parse(header);
  if (!Number.isNaN(date)) {
    const delay = date - Date.now();
    return delay > 0 ? delay : null;
  }
  return null;
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    const json = await res.json();
    // RCA-fix (2026-06-19): PATH API uses ErrorResponse(string Error) which serialises to
    // {"error":"..."}. Previous code only checked json.message and json.title so all
    // backend-supplied messages (loan not found, borrower not found, etc.) were silently
    // dropped, leaving the user with the generic res.statusText ("Not Found").
    // Now checks json.error as well so descriptive body messages reach the UI.
    //
    // NP-676: the API's GlobalExceptionHandler also returns RFC7807 ProblemDetails, whose
    // `title` is a constant per status ("Conflict" for every ConflictException) while the
    // only specific text — Vesta's own rejection reason — sits in `detail`. Reading
    // `title` first shadowed it, so every upstream failure surfaced as the bare word
    // "Conflict". `detail` must therefore outrank `title`, which stays as the fallback
    // for bodies that carry nothing else. Precedence matches auth.ts's own parser.
    return {
      status: res.status,
      message: json.message || json.error || json.detail || json.title || res.statusText,
      errors: json.errors,
      traceId: json.traceId,
    };
  } catch {
    return { status: res.status, message: res.statusText };
  }
}

// ---------- safe JSON parse ----------

async function safeJsonParse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') || '';
  // If the server explicitly returns HTML, don't try to parse it as JSON
  if (contentType.includes('text/html')) {
    throw new ApiRequestError(res.status, {
      status: res.status,
      message: `Expected JSON but received HTML — the API may not be available`,
    });
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiRequestError(res.status, {
      status: res.status,
      message: `Response is not valid JSON`,
    });
  }
}

// ---------- sleep ----------

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------- Core request ----------

export type RequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  /** Override the default retry count */
  retries?: number;
  /** Skip auth header injection */
  skipAuth?: boolean;
  /**
   * NP-532: keep the bearer token, but do NOT treat a 401 as "the session died".
   *
   * For change-password a 401 means the submitted CURRENT PASSWORD was wrong —
   * the caller is perfectly authenticated. Without this the global flow refreshes
   * the token, retries with the same wrong password, gets 401 again and logs the
   * borrower out over a typo. `skipAuth` is not a substitute: it also strips the
   * Authorization header, which this endpoint requires.
   */
  skipUnauthorizedHandling?: boolean;
  /** For file downloads */
  responseType?: 'json' | 'blob';
  signal?: AbortSignal;
  /**
   * When true, skip the global error toast even in real-auth mode.
   * Use for non-blocking background saves where the calling code handles
   * errors silently (e.g., personal-info / residency / military step saves).
   */
  suppressToast?: boolean;
};

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', headers = {}, body, retries = MAX_RETRIES, skipAuth = false, skipUnauthorizedHandling = false, responseType = 'json', signal, suppressToast = false } = opts;

  const url = `${BASE_URL}${path}`;

  const reqHeaders: Record<string, string> = { ...headers };

  if (!skipAuth) {
    const token = await getTokenFn();
    if (token) {
      reqHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  // P0 WHITE-PAGE FIX: attach a default timeout to every real API request.
  // Without this, a hung API (Azure SQL cold start, network glitch) keeps
  // isLoading=true forever — users see a blank/white page indefinitely.
  // If the caller already provides a signal, merge via AbortSignal.any() when
  // available, or fall back to using the caller's signal directly.
  let timeoutController: AbortController | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let effectiveSignal: AbortSignal | undefined = signal;
  if (!signal) {
    timeoutController = new AbortController();
    timeoutId = setTimeout(() => {
      timeoutController!.abort(new DOMException(`Request to ${path} timed out after ${DEFAULT_FETCH_TIMEOUT_MS}ms`, 'TimeoutError'));
    }, DEFAULT_FETCH_TIMEOUT_MS);
    effectiveSignal = timeoutController.signal;
  }

  let fetchInit: RequestInit = { method, headers: reqHeaders, signal: effectiveSignal };

  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      fetchInit.body = body;
      // Don't set Content-Type for FormData — browser sets boundary
    } else {
      reqHeaders['Content-Type'] = 'application/json';
      fetchInit = { ...fetchInit, headers: reqHeaders, body: JSON.stringify(body) };
    }
  }

  let lastError: ApiRequestError | Error | null = null;

  // Proactive token refresh: if token expires within 60s, refresh before sending
  if (!skipAuth && refreshTokenFn) {
    const currentToken = await getTokenFn();
    if (currentToken && isTokenExpiringSoon(currentToken, 60)) {
      try {
        if (!activeRefreshPromise) {
          activeRefreshPromise = refreshTokenFn().finally(() => {
            activeRefreshPromise = null;
          });
        }
        const refreshed = await activeRefreshPromise;
        reqHeaders['Authorization'] = `Bearer ${refreshed.accessToken}`;
        fetchInit = { ...fetchInit, headers: reqHeaders };
      } catch (refreshErr) {
        // Silent catch fix: proactive refresh failed — log so it's visible in monitoring,
        // then proceed with the existing token (request may still succeed if not yet expired)
        console.warn('[apiClient] Proactive token refresh failed — continuing with existing token:', refreshErr);
      }
    }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, fetchInit);

      if (res.ok) {
        if (timeoutId !== null) clearTimeout(timeoutId);
        if (res.status === 204) return undefined as T;
        if (responseType === 'blob') return (await res.blob()) as T;
        return await safeJsonParse<T>(res);
      }

      // Handle 429 — respect Retry-After header
      if (res.status === 429 && attempt < retries) {
        const retryDelay = parseRetryAfter(res);
        if (retryDelay !== null) {
          await sleep(Math.min(retryDelay, 30000)); // cap at 30s
          continue;
        }
      }

      // Handle 401 — attempt token refresh once (with shared promise lock)
      // Skip the entire 401 flow when onUnauthorizedFn is null (mock auth mode)
      // because there's no real backend to refresh against.
      if (res.status === 401 && !skipAuth && !skipUnauthorizedHandling && refreshTokenFn && onUnauthorizedFn) {
        try {
          // Use shared promise so concurrent 401s wait for the same refresh
          if (!activeRefreshPromise) {
            activeRefreshPromise = refreshTokenFn().finally(() => {
              activeRefreshPromise = null;
            });
          }
          const newToken = await activeRefreshPromise;
          // Retry the original request with the new token
          const retryHeaders = { ...reqHeaders, Authorization: `Bearer ${newToken.accessToken}` };
          const retryInit: RequestInit = { ...fetchInit, headers: retryHeaders };
          const retryRes = await fetch(url, retryInit);
          if (retryRes.ok) {
            if (retryRes.status === 204) return undefined as T;
            if (responseType === 'blob') return (await retryRes.blob()) as T;
            return await safeJsonParse<T>(retryRes);
          }
          // If retry also fails with 401, fall through to unauthorized handler
          if (retryRes.status === 401) {
            onUnauthorizedFn?.();
            const errBody = await parseErrorBody(retryRes);
            throw new ApiRequestError(retryRes.status, errBody);
          }
        } catch (refreshErr) {
          // Refresh itself failed — user must re-authenticate
          if (refreshErr instanceof ApiRequestError) throw refreshErr;
          onUnauthorizedFn?.();
          const errBody = await parseErrorBody(res);
          throw new ApiRequestError(res.status, errBody);
        }
      }

      // Handle 401s that were not resolved by the token-refresh flow above
      if (res.status === 401) {
        const errBody = await parseErrorBody(res);
        if (skipUnauthorizedHandling) {
          // NP-532: the caller owns this 401 (a wrong current password, not a dead
          // session) — no logout, no auth toast, just the error to handle.
          throw new ApiRequestError(res.status, errBody);
        }
        if (!onUnauthorizedFn) {
          // No unauthorized handler registered (mock-auth mode): 401s from the real API
          // are expected — log as warning only, no toast.
          // Expected in mixed mode (mock auth + real API) and before sign-in —
          // dev-only detail (console-noise cleanup 2026-07-29).
          if (import.meta.env.DEV) {
            console.debug('[API Client] 401 without an unauthorized handler (expected):', {
              path: url,
              message: errBody.message,
            });
          }
        } else {
          // Real auth mode: show toast for genuine auth failures
          toast({
            title: "Authentication Required",
            description: "Please sign in again to continue.",
            variant: "destructive",
          });
        }
        throw new ApiRequestError(res.status, errBody);
      }

      const errorBody = await parseErrorBody(res);
      lastError = new ApiRequestError(res.status, errorBody);

      // Don't retry client errors (except retryable ones)
      if (!isRetryable(res.status)) {
        throw lastError;
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        lastError = err;
        if (!isRetryable(err.status)) throw err;
      } else if (signal?.aborted && err instanceof Error && err.name === 'AbortError') {
        // NP-611 AC-5: the caller cancelled this request — only a signal it passed
        // in can be aborted from out here, so this is never the timeout above.
        // Retrying an aborted signal fails instantly three more times, and the
        // toast below would report the borrower's own cancel as a failure, so the
        // AbortError goes back untouched for the caller to recognise as a cancel.
        throw err;
      } else {
        // Network error — retryable
        lastError = withUserFriendlyError(err instanceof Error ? err : new Error(String(err)));
      }
    }

    // Exponential backoff
    if (attempt < retries) {
      const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
      const jitter = backoff * (0.5 + Math.random() * 0.5);
      await sleep(jitter);
    }
  }

  // Show toast notification on final failure
  // Suppress toasts in mock mode (no real backend) to avoid error spam
  const isMockMode = !onUnauthorizedFn;
  const isAuthError = lastError instanceof ApiRequestError && (lastError.status === 401 || lastError.status === 403);
  const mapped = mapErrorToUserMessage(lastError);
  logRawError(lastError);

  if (!isMockMode && !isAuthError && !mapped.suppressGlobalMessage && !suppressToast) {
    toast({
      title: mapped.title,
      description: mapped.message,
      variant: 'destructive',
    });

    if (lastError instanceof Error) {
      (lastError as ApiRequestError).toastShown = true;
    }
  }

  // Clean up the timeout controller regardless of outcome
  if (timeoutId !== null) clearTimeout(timeoutId);

  throw lastError ?? new Error('Request failed after retries');
}

// ---------- Public API ----------

export const apiClient = {
  get<T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(path, { ...opts, method: 'GET' });
  },

  post<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(path, { ...opts, method: 'POST', body });
  },

  put<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(path, { ...opts, method: 'PUT', body });
  },

  patch<T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(path, { ...opts, method: 'PATCH', body });
  },

  delete<T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(path, { ...opts, method: 'DELETE' });
  },

  // 🟥 `upload()` (multipart/form-data POST) REMOVED (WS9-1 Phase 6) — caller-less
  // since the API dropped multipart uploads; document uploads are JSON base64
  // via wizardService.documents.upload / borrower-me documents.

  /** Download a file as Blob */
  download(path: string, opts?: Omit<RequestOptions, 'method' | 'body' | 'responseType'>): Promise<Blob> {
    return request<Blob>(path, { ...opts, method: 'GET', responseType: 'blob' });
  },
};
